import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Button,
  Stack,
  Alert,
  Skeleton,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import { useTranslation } from 'react-i18next';
import { customerService, SalesRep } from '../../services/customerService';
import { trackEvent } from '../../services/analytics';

/**
 * "Your Sales Representative" block on the customer profile.
 *
 * Talks to `/customers/profile/sales-rep`, which resolves the current
 * user's `Customer.sales_rep_code` against the local workforce cache
 * (populated by the nightly WORKFORCE sync). Renders a mailto link
 * when the rep has an email — same click also fires a
 * `sales_rep_contacted` GA event so we can see how often customers
 * actually reach out.
 */
const SalesRepCard: React.FC = () => {
  const { t } = useTranslation();
  const [rep, setRep] = useState<SalesRep | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await customerService.getMySalesRep();
        if (!cancelled) setRep(res.sales_rep);
      } catch {
        if (!cancelled) setRep(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleEmailClick = () => {
    if (rep?.email) {
      trackEvent('sales_rep_contacted', {
        rep_code: rep.code,
        rep_role: rep.role_code || 'unknown',
      });
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
          {t('profile.salesRep.title')}
        </Typography>

        {loading ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <Skeleton variant="circular" width={56} height={56} />
            <Box flexGrow={1}>
              <Skeleton variant="text" width="40%" />
              <Skeleton variant="text" width="60%" />
            </Box>
          </Stack>
        ) : !rep ? (
          <Alert severity="info">{t('profile.salesRep.empty')}</Alert>
        ) : (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
          >
            <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
              <PersonIcon />
            </Avatar>
            <Box flexGrow={1}>
              <Typography variant="subtitle1" fontWeight="bold">
                {rep.name || rep.code}
              </Typography>
              {rep.role_description && (
                <Typography variant="body2" color="textSecondary">
                  {rep.role_description}
                </Typography>
              )}
              {rep.email && (
                <Typography variant="body2" color="textSecondary">
                  {rep.email}
                </Typography>
              )}
            </Box>
            {rep.email && (
              <Button
                variant="outlined"
                startIcon={<EmailIcon />}
                component="a"
                href={`mailto:${rep.email}`}
                onClick={handleEmailClick}
              >
                {t('profile.salesRep.sendEmail')}
              </Button>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default SalesRepCard;
